/**
 * Formular für Juristische Person / Institution (Anlegen/Bearbeiten)
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabParams } from '@/hooks/useTabRoute';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';

const TYPEN = [
  { value: 'gericht', label: 'Gericht' },
  { value: 'behoerde', label: 'Behörde' },
  { value: 'krankenversicherung', label: 'Krankenversicherung' },
  { value: 'pflegeheim', label: 'Pflegeheim' },
  { value: 'bank', label: 'Bank' },
  { value: 'versicherung', label: 'Versicherung' },
  { value: 'pflegedienst', label: 'Pflegedienst' },
  { value: 'arztpraxis', label: 'Arztpraxis' },
  { value: 'rechtsanwalt', label: 'Rechtsanwalt' },
  { value: 'arbeitgeber', label: 'Arbeitgeber' },
  { value: 'vermieter', label: 'Vermieter' },
  { value: 'glaeubigerverbindung', label: 'Gläubiger' },
  { value: 'jobcenter', label: 'Jobcenter' },
  { value: 'sozialamt', label: 'Sozialamt' },
  { value: 'rentenversicherung', label: 'Rentenversicherung' },
  { value: 'pflegekasse', label: 'Pflegekasse' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

export default function JuristischePersonFormPage() {
  const navigate = useNavigate();
  const { id } = useTabParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    typ: 'sonstiges',
    adresse: '',
    plz: '',
    ort: '',
    telefon: '',
    fax: '',
    email: '',
    website: '',
    oeffnungszeiten: '',
    ansprechpartner: '',
    ansprechpartner_telefon: '',
    ansprechpartner_email: '',
    aktenzeichen: '',
    kundennummer: '',
    notizen: ''
  });

  useEffect(() => {
    if (isEditing) {
      loadInstitution();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadInstitution = async () => {
    setLoading(true);
    try {
      const data = await betreuungAPI.getJuristischePerson(id);
      setFormData({
        name: data.name || '',
        typ: data.typ || 'sonstiges',
        adresse: data.adresse || '',
        plz: data.plz || '',
        ort: data.ort || '',
        telefon: data.telefon || '',
        fax: data.fax || '',
        email: data.email || '',
        website: data.website || '',
        oeffnungszeiten: data.oeffnungszeiten || '',
        ansprechpartner: data.ansprechpartner || '',
        ansprechpartner_telefon: data.ansprechpartner_telefon || '',
        ansprechpartner_email: data.ansprechpartner_email || '',
        aktenzeichen: data.aktenzeichen || '',
        kundennummer: data.kundennummer || '',
        notizen: data.notizen || ''
      });
    } catch (error) {
      toast.error('Institution nicht gefunden');
      navigate('/personen');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Bitte Namen eingeben');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await betreuungAPI.updateJuristischePerson(id, formData);
        toast.success('Institution aktualisiert');
      } else {
        await betreuungAPI.createJuristischePerson(formData);
        toast.success('Institution erstellt');
      }
      navigate('/personen');
    } catch (error) {
      toast.error(error.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="jur-person-form">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
        <h1 className="text-2xl font-bold text-slate-800">
          {isEditing ? 'Institution bearbeiten' : 'Neue Institution'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Stammdaten */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Stammdaten</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="name">Name / Bezeichnung *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="z.B. Amtsgericht München"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Typ</Label>
              <Select value={formData.typ} onValueChange={(v) => handleChange('typ', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {TYPEN.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Adresse */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Adresse</h2>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="adresse">Straße und Hausnummer</Label>
              <Input
                id="adresse"
                value={formData.adresse}
                onChange={(e) => handleChange('adresse', e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="plz">PLZ</Label>
                <Input
                  id="plz"
                  value={formData.plz}
                  onChange={(e) => handleChange('plz', e.target.value)}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="ort">Ort</Label>
                <Input
                  id="ort"
                  value={formData.ort}
                  onChange={(e) => handleChange('ort', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Kontakt */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Kontakt</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="telefon">Telefon</Label>
              <Input
                id="telefon"
                type="tel"
                value={formData.telefon}
                onChange={(e) => handleChange('telefon', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fax">Fax</Label>
              <Input
                id="fax"
                value={formData.fax}
                onChange={(e) => handleChange('fax', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={formData.website}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="https://"
              />
            </div>
          </div>
        </div>

        {/* Öffnungszeiten */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Öffnungszeiten</h2>
          <Textarea
            value={formData.oeffnungszeiten}
            onChange={(e) => handleChange('oeffnungszeiten', e.target.value)}
            placeholder="Mo-Fr 8:00-16:00..."
            rows={2}
          />
        </div>

        {/* Ansprechpartner */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Ansprechpartner</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ansprechpartner">Name</Label>
              <Input
                id="ansprechpartner"
                value={formData.ansprechpartner}
                onChange={(e) => handleChange('ansprechpartner', e.target.value)}
                placeholder="Herr/Frau..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ansprechpartner_telefon">Durchwahl</Label>
              <Input
                id="ansprechpartner_telefon"
                value={formData.ansprechpartner_telefon}
                onChange={(e) => handleChange('ansprechpartner_telefon', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ansprechpartner_email">E-Mail</Label>
              <Input
                id="ansprechpartner_email"
                type="email"
                value={formData.ansprechpartner_email}
                onChange={(e) => handleChange('ansprechpartner_email', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Aktenzeichen/Kundennummer */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Referenzen</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="aktenzeichen">Aktenzeichen</Label>
              <Input
                id="aktenzeichen"
                value={formData.aktenzeichen}
                onChange={(e) => handleChange('aktenzeichen', e.target.value)}
                placeholder="Az. bei dieser Institution"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="kundennummer">Kundennummer</Label>
              <Input
                id="kundennummer"
                value={formData.kundennummer}
                onChange={(e) => handleChange('kundennummer', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Notizen */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Notizen</h2>
          <Textarea
            value={formData.notizen}
            onChange={(e) => handleChange('notizen', e.target.value)}
            placeholder="Zusätzliche Notizen..."
            rows={3}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isEditing ? 'Speichern' : 'Anlegen'}
          </Button>
        </div>
      </form>
    </div>
  );
}
